import React from "react";
import useToasts from "@ui/Toast/useToasts";
import ModalForm from "@ui/Modal/Form";
import t from "../../i18n";
import { useDelete } from "../../api/useCall";
import type { Project } from "../../api/useProjects";
import useInput from "@ui/lib/hooks/useInput";
import InputV2 from "@ui/Input/InputV2";
import { useMutateBody } from "../../api/useQuery";

interface P {
  project: Project;
  onRequestClose: () => void;
}

const DeleteProjectModalForm = ({ project, onRequestClose }: P) => {
  const deleteProject = useDelete("/projects/:projectId(uuid)");
  const { addToast } = useToasts();
  const { value: projectNameValue, onChange: projectNameOnChange } = useInput("");
  const mutateProjects = useMutateBody("/projects");

  const handleSubmit = React.useCallback(() => {
    addToast("info", t("projects.modal.toast.delete_project_in_progress", { name: project.name }));
    const rollback = mutateProjects(({ collection, ...rest }) => ({
      ...rest,
      collection: collection.filter((p: Project) => p.id !== project.id),
    }));

    deleteProject({ routeParams: { projectId: project.id } })
      .then(() => {
        addToast("success", t("projects.modal.toast.delete_project", { name: project.name }));
      })
      .catch(error => {
        rollback();
        throw error;
      });

    return true;
  }, [addToast, deleteProject, project.id, project.name, mutateProjects]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={projectNameValue === project.name ? handleSubmit : false}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
      focusCancel
    >
      <div>{t("projects.modal.delete.are_you_sure", { name: project.name })}</div>
      <InputV2
        label={t("projects.modal.delete.project_name")}
        required
        placeholder={t("projects.modal.delete.input_placeholder")}
        value={projectNameValue}
        onChange={projectNameOnChange}
      />
    </ModalForm>
  );
};

export default React.memo(DeleteProjectModalForm);
